/**
 * Policy Lightbox Modal
 * Handles dynamic loading and display of policy pages in a modal overlay
 */
class PolicyLightbox {
  constructor() {
    this.lightbox = null;
    this.overlay = null;
    this.container = null;
    this.title = null;
    this.loading = null;
    this.body = null;
    this.closeBtn = null;
    
    this.isOpen = false;
    this.cache = new Map();
    
    this.init();
  }
  
  init() {
    // Create lightbox if it doesn't exist
    if (!document.getElementById('policy-lightbox')) {
      console.warn('Policy lightbox element not found in DOM');
      return;
    }
    
    this.lightbox = document.getElementById('policy-lightbox');
    this.overlay = this.lightbox.querySelector('.policy-lightbox-overlay');
    this.container = this.lightbox.querySelector('.policy-lightbox-container');
    this.title = this.lightbox.querySelector('.policy-lightbox-title');
    this.loading = this.lightbox.querySelector('.policy-lightbox-loading');
    this.body = this.lightbox.querySelector('.policy-lightbox-body');
    this.closeBtn = this.lightbox.querySelector('.policy-lightbox-close');
    
    this.bindEvents();
    this.interceptPolicyLinks();
  }
  
  bindEvents() {
    // Close button
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.close());
    }
    
    // Overlay click
    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }
    
    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });
    
    // Prevent body scroll when modal is open
    this.lightbox.addEventListener('transitionend', () => {
      if (this.isOpen) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
    });
  }
  
  interceptPolicyLinks() {
    // Find all policy links and add click handlers
    document.addEventListener('click', (e) => {
      const link = e.target.closest('a[href]');
      if (!link) return;
      
      const href = link.getAttribute('href');
      if (!href) return;
      
      // Only trigger lightbox for specific consent/agreement links
      const shouldTriggerLightbox = link.classList.contains('terms_link') || this.isConsentPolicyLink(link);
      
      if (shouldTriggerLightbox) {
        e.preventDefault();
        
        // Get policy title from URL or data attributes
        const policyTitle = this.getPolicyTitleFromLink(link);
        
        // Check for custom content
        const customContent = link.getAttribute('data-custom-content');
        if (customContent) {
          this.loadPolicy(href, policyTitle, customContent);
        } else {
          this.loadPolicy(href, policyTitle);
        }
      }
    });
  }
  
  // isConsentPolicyLink(link) {
  //   // Check if link is within consent/agreement contexts
  //   const consentContainers = [
  //     '.terms_checkbox',           // Consent checkboxes
  //     '.hdt-cart-tax',            // Cart tax/policy area
  //     '.hdt-agree_email',         // Newsletter consent
  //     '.hdt-form-input_wrap',     // Contact form consent
  //     '.cart__page',              // Cart page consent
  //     '.cart_drawer'              // Cart drawer consent
  //   ];
    
  //   // Check if link is within any consent container
  //   const isInConsentContext = consentContainers.some(selector => 
  //     link.closest(selector) !== null
  //   );
    
  //   if (!isInConsentContext) return false;
    
  //   // Additional check: only policy URLs in consent contexts
  //   const href = link.getAttribute('href');
  //   if (!href) return false;
    
  //   // Handle custom content
  //   if (href.startsWith('#custom-content-')) {
  //     return true;
  //   }
    
  //   const policyPatterns = [
  //     '/policies/',
  //     '/pages/privacy',
  //     '/pages/terms',  
  //     '/pages/refund',
  //     '/pages/shipping',
  //     '/pages/contact'
  //   ];
    
  //   return policyPatterns.some(pattern => href.includes(pattern));
  // }
  isConsentPolicyLink(link) {
    // Only trigger lightbox for links with specific indicators:
    
    // 1. Links with 'terms_link' class (generated by Liquid processor)
    if (link.classList.contains('terms_link')) {
      return true;
    }
    
    // 2. Links with data attributes (custom content or policy title)
    if (link.hasAttribute('data-custom-content') || 
        link.hasAttribute('data-policy-title')) {
      return true;
    }
    
    // 3. Custom content links
    const href = link.getAttribute('href');
    if (href && href.startsWith('#custom-content-')) {
      return true;
    }
    
    // For all other links (direct policy links without above attributes),
    // let them redirect normally
    return false;
  }
  
  getPolicyTitleFromLink(link) {
    const href = link.getAttribute('href');
    
    // First check if there's a data-policy-title attribute
    const dataPolicyTitle = link.getAttribute('data-policy-title');
    if (dataPolicyTitle) {
      return dataPolicyTitle;
    }
    
    // For custom content, try to get custom title
    if (href && href.startsWith('#custom-content-')) {
      const customTitle = link.getAttribute('data-custom-title');
      if (customTitle) {
        return customTitle;
      }
    }
    
    // Extract policy type from URL and convert to readable title
    if (href) {
      // Handle Shopify policy URLs
      if (href.includes('/policies/privacy-policy')) {
        return 'Privacy Policy';
      } else if (href.includes('/policies/terms-of-service')) {
        return 'Terms of Service';
      } else if (href.includes('/policies/refund-policy')) {
        return 'Refund Policy';
      } else if (href.includes('/policies/shipping-policy')) {
        return 'Shipping Policy';
      } else if (href.includes('/policies/')) {
        // Generic policy URL - extract name from path
        const policyName = href.split('/policies/')[1]?.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        if (policyName) {
          return policyName;
        }
      }
      
      // Handle page URLs
      if (href.includes('/pages/privacy')) {
        return 'Privacy Policy';
      } else if (href.includes('/pages/terms')) {
        return 'Terms of Service';
      } else if (href.includes('/pages/refund')) {
        return 'Refund Policy';
      } else if (href.includes('/pages/shipping')) {
        return 'Shipping Policy';
      } else if (href.includes('/pages/contact')) {
        return 'Contact Information';
      } else if (href.includes('/pages/')) {
        // Generic page URL - extract name from path
        const pageName = href.split('/pages/')[1]?.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        if (pageName) {
          return pageName;
        }
      }
    }
    
    // Fallback: return generic policy name
    return 'Policy Information';
  }
  
  async loadPolicy(url, title = 'Policy', customContent = null) {
    try {
      this.open(title);
      this.showLoading();
      
      // Handle custom content
      if (customContent) {
        this.displayContent(customContent);
        return;
      }
      
      // Check cache first
      if (this.cache.has(url)) {
        this.displayContent(this.cache.get(url));
        return;
      }
      
      // Fetch policy content
      const response = await fetch(url, {
        headers: {
          'Accept': 'text/html',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const html = await response.text();
      const content = this.extractPolicyContent(html);
      
      // Cache the content
      this.cache.set(url, content);
      
      this.displayContent(content);
      
    } catch (error) {
      console.error('Failed to load policy:', error);
      this.displayError('Failed to load content. Please try again later.');
    }
  }

  extractPolicyContent(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    
    // Primary: Try to get shopify-policy__body first (most specific)
    let policyBody = doc.querySelector('.shopify-policy__body');
    if (policyBody && policyBody.textContent.trim().length > 0) {
      return this.cleanContent(policyBody.innerHTML);
    }
    
    // Secondary: Try other policy-specific selectors
    const policySelectors = [
      '.shopify-policy__container .rte', // Policy content within container
      '.policy-content',
      '.page-content .rte',
      '.template-policy .rte'
    ];
    for (const selector of policySelectors) {
      const element = doc.querySelector(selector);
      if (element && element.textContent.trim().length > 50) {
        return this.cleanContent(element.innerHTML);
      }
    }
    // Fallback: Extract from main content but exclude unwanted elements
    const mainSelectors = [
      'main .page',
      'main .template-page', 
      '[role="main"]',
      'main'
    ];
    
    for (const selector of mainSelectors) {
      const element = doc.querySelector(selector);
      if (element) {
        // Remove unwanted elements before extracting
        const unwantedElements = element.querySelectorAll(
          '.shopify-policy__title, .page-title, .breadcrumbs, nav, header, footer, .navigation, .page-header'
        );
        unwantedElements.forEach(el => el.remove());
        const cleanContent = element.textContent.trim();
        if (cleanContent.length > 50) {
          return this.cleanContent(element.innerHTML);
        }
      }
    }
    return '<p>Content not available.</p>';
  }
  cleanContent(html) {
    const temp = document.createElement('div');
    temp.innerHTML = html;
    
    // Remove Shopify policy-specific unwanted elements
    const unwantedSelectors = [
      'script', 'style', 'nav', 'header', 'footer',
      '.shopify-policy__title',      // Policy title
      '.shopify-policy__header',     // Policy header
      '.breadcrumbs', 
      '.navigation', 
      '.sidebar',
      '.page-header', 
      '.page-title', 
      'h1.page-title',
      '.page-width > h1:first-child', // First h1 in page-width
      '.main-page-title'
    ];
    
    unwantedSelectors.forEach(selector => {
      const elements = temp.querySelectorAll(selector);
      elements.forEach(el => el.remove());
    });
    
    // Clean up attributes
    const allElements = temp.querySelectorAll('*');
    allElements.forEach(el => {
      const allowedAttrs = ['href', 'src', 'alt', 'title', 'class'];
      const attrs = Array.from(el.attributes);
      attrs.forEach(attr => {
        if (!allowedAttrs.includes(attr.name)) {
          el.removeAttribute(attr.name);
        }
      });
    });
    
    return temp.innerHTML;
  }
  
  open(title) {
    this.title.textContent = title;
    this.lightbox.style.display = 'block';
    
    // Force reflow
    this.lightbox.offsetHeight;
    
    this.lightbox.classList.add('active');
    this.isOpen = true;
    
    // Focus management
    this.container.focus();
    this.container.setAttribute('tabindex', '-1');
    
    // Trap focus within modal
    this.trapFocus();
  }
  
  close() {
    this.lightbox.classList.remove('active');
    this.isOpen = false;
    
    // Hide after transition
    setTimeout(() => {
      if (!this.isOpen) {
        this.lightbox.style.display = 'none';
        document.body.style.overflow = '';
      }
    }, 300);
    
    // Return focus to triggering element if possible
    this.returnFocus();
  }
  
  showLoading() {
    this.loading.style.display = 'flex';
    this.body.style.display = 'none';
  }
  
  displayContent(content) {
    this.loading.style.display = 'none';
    this.body.innerHTML = content;
    this.body.style.display = 'block';
    
    // Scroll to top
    this.body.scrollTop = 0;
  }
  
  displayError(message) {
    this.loading.style.display = 'none';
    this.body.innerHTML = `
      <div style="text-align: center; padding: 40px 20px;">
        <p style="color: #dc3545; margin-bottom: 16px;">${message}</p>
        <button onclick="window.policyLightbox.close()" style="padding: 8px 16px; border: 1px solid #ccc; background: none; border-radius: 4px; cursor: pointer;">Close</button>
      </div>
    `;
    this.body.style.display = 'block';
  }
  
  trapFocus() {
    const focusableElements = this.container.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    
    if (focusableElements.length === 0) return;
    
    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];
    
    this.container.addEventListener('keydown', (e) => {
      if (e.key === 'Tab') {
        if (e.shiftKey) {
          if (document.activeElement === firstFocusable) {
            e.preventDefault();
            lastFocusable.focus();
          }
        } else {
          if (document.activeElement === lastFocusable) {
            e.preventDefault();
            firstFocusable.focus();
          }
        }
      }
    });
  }
  
  returnFocus() {
    // Try to return focus to the element that opened the modal
    if (document.activeElement && document.activeElement !== document.body) {
      document.activeElement.blur();
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.policyLightbox = new PolicyLightbox();
});

// Also initialize if DOM is already loaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    if (!window.policyLightbox) {
      window.policyLightbox = new PolicyLightbox();
    }
  });
} else {
  if (!window.policyLightbox) {
    window.policyLightbox = new PolicyLightbox();
  }
}